import { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@features/auth/store/authStore';
import { authService } from '@features/auth/services/authService';
import { Spinner } from '@components/ui';
import type { User } from '@features/auth/types/auth.types';

function clearStoredAuth() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('sessionId');
  localStorage.removeItem('selectedCompanyId');
  localStorage.removeItem('authUser');
  localStorage.removeItem('invoice-filter-storage');
}

export function ProtectedRoute() {
  const { isAuthenticated, setAuth } = useAuthStore();
  const [isRehydrating, setIsRehydrating] = useState(!isAuthenticated && !!localStorage.getItem('accessToken'));

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const sessionId = localStorage.getItem('sessionId');

    if (!isAuthenticated && accessToken && sessionId) {
      // Tokens exist in localStorage but Zustand lost state (page refresh)
      authService.getCurrentUser()
        .then((user) => {
          setAuth(user, accessToken, sessionId);
        })
        .catch((error) => {
          const status = error?.response?.status;
          const storedUserRaw = localStorage.getItem('authUser');

          // Preserve local auth snapshot if the backend is temporarily unavailable.
          if ((status === 503 || !error?.response) && storedUserRaw) {
            try {
              const storedUser = JSON.parse(storedUserRaw) as User;
              setAuth(storedUser, accessToken, sessionId);
              return;
            } catch {
              // Fall through to clean-up if local snapshot is invalid.
            }
          }

          clearStoredAuth();
        })
        .finally(() => {
          setIsRehydrating(false);
        });
      return;
    }

    if (!isAuthenticated && accessToken && !sessionId) {
      clearStoredAuth();
    }

    setIsRehydrating(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isRehydrating) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="protected-route-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
